/* eslint-disable */
// @ts-nocheck
import { useEffect, useRef, useCallback } from 'react'

export function useSwipeProgress (
  containerRef,
  onProgress,
  bannersLength,
  transitionSpeed = 500 // must match the slider's `speed` setting
) {
  const dragRafRef = useRef(0)
  const animRafRef = useRef(0)
  const snapbackTimerRef = useRef()
  const lastProgressRef = useRef(0)

  // keep latest callback/length without re-running the effect
  const onProgressRef = useRef(onProgress)
  onProgressRef.current = onProgress
  const lengthRef = useRef(bannersLength)
  lengthRef.current = bannersLength

  // animates progress from its current value to a target over time
  const animateTo = useCallback((targetSign, fromT) => {
    cancelAnimationFrame(animRafRef.current)
    const duration = Math.max(1, transitionSpeed * (1 - fromT))
    const start = performance.now()
    const step = (now) => {
      const t = Math.min(1, fromT + (1 - fromT) * ((now - start) / duration))
      lastProgressRef.current = targetSign * t
      onProgressRef.current(targetSign * t)
      if (t < 1) {
        animRafRef.current = requestAnimationFrame(step)
      } else {
        lastProgressRef.current = 0
      }
    }
    animRafRef.current = requestAnimationFrame(step)
  }, [transitionSpeed])

  // eases progress back to 0 (released below swipe threshold)
  const easeBackToZero = useCallback(() => {
    const startP = lastProgressRef.current
    if (startP === 0) return
    cancelAnimationFrame(animRafRef.current)
    const duration = Math.max(1, transitionSpeed * Math.abs(startP))
    const start = performance.now()
    const step = (now) => {
      const k = Math.min(1, (now - start) / duration)
      const p = startP * (1 - k)
      lastProgressRef.current = p
      onProgressRef.current(p)
      if (k < 1) animRafRef.current = requestAnimationFrame(step)
    }
    animRafRef.current = requestAnimationFrame(step)
  }, [transitionSpeed])

  // call from slick's beforeChange — covers autoplay AND drag-commit snaps
  const onBeforeChange = useCallback((from, to) => {
    const n = lengthRef.current
    if (n === 0) return
    clearTimeout(snapbackTimerRef.current)
    cancelAnimationFrame(dragRafRef.current)
    const forward = to === (from + 1) % n
    const sign = forward ? 1 : -1
    const last = lastProgressRef.current
    // continue from the drag position if the drag was in the same direction
    const fromT = Math.sign(last) === sign ? Math.min(1, Math.abs(last)) : 0
    animateTo(sign, fromT)
  }, [animateTo])

  useEffect(() => {
    const container = containerRef.current
    if (container === null || bannersLength === 0) return

    const list = container.querySelector('.slick-list')
    const track = container.querySelector('.slick-track')
    if (list === null || track === null) return

    let startTranslate = 0
    let slideWidth = 0
    let dragging = false

    const getTranslateX = () => {
      const m = /translate3d\((-?[\d.]+)px/.exec(track.style.transform)
      return m !== null ? parseFloat(m[1]) : 0
    }

    const loop = () => {
      if (!dragging) return
      const delta = getTranslateX() - startTranslate
      const progress = Math.max(-1, Math.min(1, -delta / slideWidth))
      lastProgressRef.current = progress
      onProgressRef.current(progress)
      dragRafRef.current = requestAnimationFrame(loop)
    }

    const onStart = () => {
      clearTimeout(snapbackTimerRef.current)
      cancelAnimationFrame(animRafRef.current)
      dragging = true
      slideWidth = list?.offsetWidth
      startTranslate = getTranslateX()
      dragRafRef.current = requestAnimationFrame(loop)
    }

    const onEnd = () => {
      dragging = false
      cancelAnimationFrame(dragRafRef.current)
      // if slick commits a slide change, beforeChange fires right after
      // and cancels this; otherwise it's a snap-back — ease to 0
      snapbackTimerRef.current = setTimeout(easeBackToZero, 60)
    }

    list.addEventListener('touchstart', onStart, { passive: true })
    list.addEventListener('touchend', onEnd, { passive: true })
    list.addEventListener('touchcancel', onEnd, { passive: true })

    return () => {
      clearTimeout(snapbackTimerRef.current)
      cancelAnimationFrame(dragRafRef.current)
      cancelAnimationFrame(animRafRef.current)
      list.removeEventListener('touchstart', onStart)
      list.removeEventListener('touchend', onEnd)
      list.removeEventListener('touchcancel', onEnd)
    }
  }, [containerRef, bannersLength, easeBackToZero])

  return { onBeforeChange }
}

export const lerpColor = (a, b, t) => {
  const toRgb = (hex) => {
    const n = parseInt(hex.replace('#', ''), 16)
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
  }
  const [r1, g1, b1] = toRgb(a)
  const [r2, g2, b2] = toRgb(b)
  return `rgb(${Math.round(r1 + (r2 - r1) * t)}, ${Math.round(g1 + (g2 - g1) * t)}, ${Math.round(b1 + (b2 - b1) * t)})`
}
